import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, Output, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import {
  ActivityAssociationInterface,
  ActivityInterface,
  CrmObjectInterface,
  FieldValueInterface,
  StringListInterface,
} from '@vendasta/crm';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { User } from '@vendasta/iamv2';
import { switchWhenFalsy } from '@vendasta/rx-utils';
import { ObservableWorkState } from '@vendasta/rx-utils/work-state';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  combineLatest,
  firstValueFrom,
  map,
  of,
  tap,
  from,
  shareReplay,
} from 'rxjs';
import { distinctUntilChanged, filter, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { ChipListComponent, ChipListItem } from '../../chip-list';
import { DropDownOptionsButton } from '../../chip-list/interfaces';
import { CrmUnknownUserAvatarComponent } from '../../unknown-user-avatar/unknown-user-avatar.component';
import {
  CrmInjectionToken,
  CrmDependencies,
  ActivityType,
  CommunicationSubType,
  ObjectType,
} from '../../../tokens-and-interfaces';
import { AVATAR_WIDTH, SNACKBAR_DURATION } from '../../../constants';
import { ObjectAssociationType } from '../../../tokens-and-interfaces';
import { CrmTruncatePipe } from '../../../shared-pipes/truncate.pipe';
import { TranslationModule } from '../../../i18n/translation-module';
import { ActivityLoggerService } from './activity-logger.service';
import {
  LoggerAssociation,
  CrmActivityLoggerCallComponent,
  CrmActivityLoggerCompanyAssociationComponent,
  CrmActivityLoggerContactAssociationComponent,
  CrmActivityLoggerEmailComponent,
  CrmActivityLoggerMeetingComponent,
  CrmActivityLoggerNoteComponent,
  CrmActivityLoggerOpportunityAssociationComponent,
  CrmActivityLoggerTagsComponent,
  CrmActivityLoggerTaskComponent,
  CrmActivityLoggerTeamAssociationComponent,
  CrmLoggerContentDirective,
  LoggerComponentInterface,
  CrmActivityLoggerFollowUpTaskComponent,
} from './logger';
import { CrmActivityLoggerCommunicationComponent } from './logger/communication.component';
import {
  CrmObjectService,
  CrmFieldService,
  CrmAssociationService,
  StandardIds,
  PlatformExtensionFieldIds,
  StandardExternalIds,
} from '../../../shared-services';

const DEFAULT_SELECTED_ACTIVITY_TYPE: ActivityType = 'Note';
// matches the `sm` column size
const DEFAULT_MOBILE_BREAKPOINT = '600px';

type SelectableActivityType = ChipListItem<ActivityType>;

const SELECTABLE_ACTIVITY_TYPES: SelectableActivityType[] = [
  {
    value: 'Note',
    label: 'ACTIVITY_TYPES.NOTE',
    icon: 'notes',
  },
  {
    value: 'Email',
    label: 'ACTIVITY_TYPES.EMAIL',
    icon: 'email',
  },
  {
    value: 'Call',
    label: 'ACTIVITY_TYPES.CALL',
    icon: 'phone',
  },
  {
    value: 'Meeting',
    label: 'ACTIVITY_TYPES.MEETING',
    icon: 'calendar_today',
  },
  {
    value: 'Task',
    label: 'ACTIVITY_TYPES.TASK',
    icon: 'assignment_turned_in',
  },
];

type SelectableCommunicationType = DropDownOptionsButton<CommunicationSubType>;

const SELECTABLE_COMMUNICATION_TYPES: SelectableCommunicationType[] = [
  {
    value: 'SMS',
    label: 'ACTIVITY_TYPES.COMMUNICATION_SUBTYPES.SMS',
  },
  {
    value: 'Inbox',
    label: 'ACTIVITY_TYPES.COMMUNICATION_SUBTYPES.INBOX',
  },
  {
    value: 'LinkedIn',
    label: 'ACTIVITY_TYPES.COMMUNICATION_SUBTYPES.LINKEDIN',
  },
];

type Layout = 'auto' | 'mobile';

const manuallyLoggedFieldValue: FieldValueInterface = {
  fieldId: StandardIds.ActivityManuallyLogged,
  booleanValue: true,
};

@Component({
  selector: 'crm-activity-logger',
  templateUrl: './activity-logger.component.html',
  styleUrls: ['./activity-logger.component.scss', './logger/shared.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    MatCardModule,
    MatIconModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatButtonModule,
    GalaxyAvatarModule,
    GalaxyFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    CrmUnknownUserAvatarComponent,
    MatTooltipModule,
    GalaxyButtonLoadingIndicatorModule,
    ChipListComponent,
    CrmLoggerContentDirective,
    CrmActivityLoggerNoteComponent,
    CrmActivityLoggerEmailComponent,
    CrmActivityLoggerCallComponent,
    CrmActivityLoggerMeetingComponent,
    CrmActivityLoggerTaskComponent,
    CrmTruncatePipe,
    CrmActivityLoggerContactAssociationComponent,
    CrmActivityLoggerCompanyAssociationComponent,
    CrmActivityLoggerTeamAssociationComponent,
    CrmActivityLoggerOpportunityAssociationComponent,
    CrmActivityLoggerCommunicationComponent,
    MatMenuModule,
    CrmActivityLoggerTagsComponent,
    CrmActivityLoggerFollowUpTaskComponent,
  ],
  providers: [ActivityLoggerService],
  standalone: true,
})
export class CrmActivityLoggerComponent {
  @ViewChild(CrmActivityLoggerNoteComponent) loggerNoteComponent?: CrmActivityLoggerNoteComponent;
  @ViewChild(CrmActivityLoggerEmailComponent) loggerEmailComponent?: CrmActivityLoggerEmailComponent;
  @ViewChild(CrmActivityLoggerCallComponent) loggerCallComponent?: CrmActivityLoggerCallComponent;
  @ViewChild(CrmActivityLoggerMeetingComponent) loggerMeetingComponent?: CrmActivityLoggerMeetingComponent;
  @ViewChild(CrmActivityLoggerTaskComponent) loggerTaskComponent?: CrmActivityLoggerTaskComponent;
  @ViewChild(CrmActivityLoggerCommunicationComponent)
  loggerCommunicationComponent?: CrmActivityLoggerCommunicationComponent;
  @ViewChild(CrmActivityLoggerFollowUpTaskComponent)
  loggerFollowUpTaskComponent?: CrmActivityLoggerFollowUpTaskComponent;

  AVATAR_WIDTH = AVATAR_WIDTH;
  SELECTABLE_ACTIVITY_TYPES = SELECTABLE_ACTIVITY_TYPES;
  SELECTABLE_COMMUNICATION_TYPES = SELECTABLE_COMMUNICATION_TYPES;
  activityTypeSelected?: ActivityType;
  communicationSubTypeSelected?: CommunicationSubType;
  collapsed = signal<boolean>(true);
  sharedDescription: string | null = '';

  private readonly layout$$: BehaviorSubject<Layout> = new BehaviorSubject<Layout>('auto');
  readonly mobileLayout$: Observable<boolean>;

  private readonly objectType$$ = new ReplaySubject<ObjectType>(1);
  protected objectType$ = this.objectType$$.asObservable();
  protected isCompany$: Observable<boolean> = this.objectType$.pipe(map((objectType) => objectType === 'Company'));
  @Input({ required: true }) set objectType(value: ObjectType) {
    this.objectType$$.next(value);
  }

  private readonly crmObjectId$$ = new ReplaySubject<string>(1);
  protected crmObjectId$ = this.crmObjectId$$.asObservable();
  @Input({ required: true }) set crmObjectId(value: string) {
    this.crmObjectId$$.next(value);
  }

  public user$ = this.activityLoggerService.user$;
  protected activityOwnerId$: Observable<string>;
  @Input() activityOwner?: User;

  /**
   * Associations to be added to the activity. These are not visible to the user.
   */
  @Input() extraAssociations?: ActivityAssociationInterface[];
  /**
   * Hide Company and Contact association fields.
   */
  @Input() hideAssociations = false;

  /**
   * Expand and select the activity type when provided.
   */
  @Input() set expandAndSelectType(value: string | undefined) {
    const activityType = value ? (value as ActivityType) : DEFAULT_SELECTED_ACTIVITY_TYPE;
    this.onActivityTypeChanged(activityType);
  }

  /**
   * Layout of the activity logger.
   * Use `mobile` to display the activity logger in a mobile layout disregarding the screen size.
   */
  @Input() set layout(value: Layout) {
    this.layout$$.next(value);
  }
  /**
   * When an Activity ID is passed in assume we are editing.
   */
  private readonly crmActivityId$$ = new BehaviorSubject<string>('');
  protected crmActivityId$ = this.crmActivityId$$.asObservable();
  protected isEditing$: Observable<boolean>;
  @Input() set crmActivityId(value: string) {
    this.crmActivityId$$.next(value);
  }

  @Output() activityCreated: EventEmitter<ActivityInterface> = new EventEmitter();
  @Output() activityUpdated: EventEmitter<ActivityInterface> = new EventEmitter();
  @Output() cancel: EventEmitter<void> = new EventEmitter();

  activity$$ = new BehaviorSubject<ActivityInterface | null>(null);
  activity$ = this.activity$$.asObservable();

  associations = signal<LoggerAssociation[]>([]);
  contactAssociations: LoggerAssociation[] = [];
  companyAssociations: LoggerAssociation[] = [];
  teamAssociations: LoggerAssociation[] = [];
  opportunityAssociations: LoggerAssociation[] = [];

  tags$$ = new BehaviorSubject<string[]>([]);
  tags$ = this.tags$$.asObservable();

  private readonly upsertActivityWorkState$ = new ObservableWorkState<string[]>();
  protected readonly upsertActivityButtonLoading$ = this.upsertActivityWorkState$.isLoading$;

  protected showTeamMemberInput = false;
  protected showOpportunityInput = false;
  protected readonly primaryAccountGroupId$: Observable<string | null>;
  private readonly crmObjectName$: Observable<string>;
  private readonly crmObject$: Observable<CrmObjectInterface>;
  private readonly followUpExpanded$$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private activityLoggerService: ActivityLoggerService,
    private crmObjectService: CrmObjectService,
    private readonly snackService: SnackbarService,
    private readonly fieldsService: CrmFieldService,
    private readonly associationService: CrmAssociationService,
    private readonly breakpointObserver: BreakpointObserver,
  ) {
    this.crmActivityId$
      .pipe(
        takeUntilDestroyed(),
        switchMap((crmActivityId) =>
          crmActivityId ? this.activityLoggerService.getActivity(crmActivityId) : of(null),
        ),
        tap((activity) => {
          this.activity$$.next(activity);
          if (activity) {
            const tags = this.fieldsService.getFieldValueFromCrmObject(activity, StandardIds.ActivityTags);
            this.tags$$.next(tags?.stringValues?.values || []);
          } else {
            this.tags$$.next([]);
          }
        }),
      )
      .subscribe();
    this.activityOwnerId$ = combineLatest([this.activity$, this.user$]).pipe(
      takeUntilDestroyed(),
      map(([activity, user]): string => activity?.ownerId || user?.userId || ''),
      distinctUntilChanged(),
    );

    this.isEditing$ = this.crmActivityId$.pipe(map((crmActivityId) => Boolean(crmActivityId)));
    this.showTeamMemberInput = Boolean(this.config?.services?.userService?.searchUsers);
    this.showOpportunityInput = Boolean(this.config?.services?.opportunityService);
    this.crmObject$ = combineLatest([this.crmObjectId$, this.objectType$]).pipe(
      switchMap(([crmObjectId, crmObjectType]) => this.crmObjectService.getMultiObject(crmObjectType, [crmObjectId])),
      map((response) => response?.crmObjects?.[0]),
      filter(Boolean),
      shareReplay(1),
    );

    this.crmObjectName$ = this.crmObject$.pipe(map((crmObject) => this.getObjectName(crmObject)));
    this.primaryAccountGroupId$ = this.crmObject$.pipe(map((crmObject) => this.getPrimaryAccountGroupId(crmObject)));

    this.mobileLayout$ = this.setupMobileMatcher();

    this.upsertActivityWorkState$.workResults$
      .pipe(
        takeUntilDestroyed(),
        switchMap((crmIds) => from(crmIds)),
        mergeMap((crmId) => this.activityLoggerService.getActivity(crmId)),
        tap((activity) => {
          const wasCreated = activity?.created?.getTime() === activity?.updated?.getTime();
          if (wasCreated) {
            this.activityCreated.emit(activity);
          } else {
            this.activityUpdated.emit(activity);
          }
        }),
      )
      .subscribe();

    this.upsertActivityWorkState$.successEvents$.pipe(takeUntilDestroyed()).subscribe(async (success) => {
      const isEditing = await firstValueFrom(this.isEditing$);
      const successMsg = isEditing ? 'ACTIVITY_LOGGER.EDIT_SUCCESS_MESSAGE' : 'ACTIVITY_LOGGER.SUCCESS_MESSAGE';
      const errorMsg = isEditing ? 'ACTIVITY_LOGGER.EDIT_ERROR_MESSAGE' : 'ACTIVITY_LOGGER.ERROR_MESSAGE';
      if (!success) {
        this.snackService.openErrorSnack(errorMsg, {
          duration: SNACKBAR_DURATION,
        });
      } else {
        this.snackService.openSuccessSnack(successMsg, {
          duration: SNACKBAR_DURATION,
        });
        if (!isEditing) this.collapse();
      }
    });

    this.intializeDefaultActivity();
  }

  private intializeDefaultActivity() {
    toObservable(this.collapsed)
      .pipe(
        takeUntilDestroyed(),
        filter((isCollapsed) => !isCollapsed),
        withLatestFrom(this.isEditing$),
        switchMap(([, isEditing]) => {
          if (isEditing) {
            // use existing activity data
            return this.crmActivityId$.pipe(
              switchMap((crmActivityId) => this.activityLoggerService.getActivity(crmActivityId)),
            );
          } else {
            // set default values for new activity
            return combineLatest([this.objectType$, this.crmObjectId$, this.user$]).pipe(
              switchMap(([objectType, crmObjectId, user]) => {
                const currentUserId = user?.userId || '';
                const defaultActivity = {
                  ownerId: currentUserId,
                  associations: [
                    {
                      crmObjectId: crmObjectId,
                      crmObjectType: objectType,
                    },
                    {
                      crmObjectId: currentUserId,
                      crmObjectType: 'User',
                    },
                  ],
                } as ActivityInterface;

                // When objectType is Contact, add the associated Companies as well
                if (objectType === 'Contact') {
                  return this.associationService.getCompanyAssociationObjectIdsForContact$(crmObjectId).pipe(
                    map((companyObjectIds) => {
                      const formattedAsc = companyObjectIds.map((companyId) => {
                        return {
                          crmObjectId: companyId,
                          crmObjectType: 'Company',
                        } as LoggerAssociation;
                      });
                      defaultActivity.associations = [...(defaultActivity?.associations || []), ...formattedAsc];
                      return defaultActivity;
                    }),
                  );
                }

                return of(defaultActivity);
              }),
            );
          }
        }),
      )
      .subscribe((activity?: ActivityInterface) => {
        // set the default values for the logger
        const activityType = activity?.crmObjectSubtype as ActivityType;
        if (activityType) {
          this.onActivityTypeChanged(activityType);
        }
        if (activity) {
          this.updateValueAndValidity();
          if (activity.associations) {
            this.setAssociationValues(activity.associations);
          }
        }
      });
  }

  onActivityTypeChanged(value: ActivityType | null) {
    this.expand();
    this.activityTypeSelected = value || undefined;
    this.communicationSubTypeSelected = undefined;
  }

  onCommunicationTypeChanged(activityType: ActivityType, communicationSubType: CommunicationSubType) {
    this.expand();
    this.activityTypeSelected = activityType;
    this.communicationSubTypeSelected = communicationSubType;
  }

  getTranslatedCommunicationType(): string {
    return this.communicationSubTypeSelected
      ? `ACTIVITY_TYPES.COMMUNICATION_SUBTYPES.${this.communicationSubTypeSelected.toUpperCase()}`
      : 'ACTIVITY_TYPES.MORE';
  }

  upsertActivity() {
    if (!this.validateForm()) {
      this.snackService.openErrorSnack('ACTIVITY_LOGGER.ERROR_SET_REQUIRED_FIELDS', {
        duration: SNACKBAR_DURATION,
      });
      return;
    }

    const fieldValues = this.getSelectedComponent()?.getValues() || [];
    const ownerId = this.getSelectedComponent()?.ownerId;
    const work$ = combineLatest([this.isEditing$, this.tags$, this.crmObjectName$, this.followUpExpanded$$]).pipe(
      switchMap(([isEditing, tags, crmObjectName, isFollowUpExpanded]) => {
        const tagsField: FieldValueInterface = {
          fieldId: StandardIds.ActivityTags,
          stringValues: <StringListInterface>{ values: tags },
        };
        let apiCalls: Observable<string>[];
        if (isEditing) {
          apiCalls = [
            this.crmActivityId$.pipe(
              switchMap((activityId) => {
                const fieldMask = [
                  'associations',
                  ...this.fieldsService.getFieldMaskFromFormGroup(this.getSelectedComponent()?.formGroup),
                  StandardIds.ActivityTags,
                ];
                return this.activityLoggerService.updateActivity(
                  activityId,
                  this.activityTypeSelected!,
                  [...fieldValues, tagsField],
                  this.mergeAssociations(),
                  ownerId,
                  fieldMask,
                );
              }),
            ),
          ];
        } else {
          apiCalls = [
            this.activityLoggerService.addActivity(
              this.activityTypeSelected!,
              [...fieldValues, manuallyLoggedFieldValue, tagsField],
              this.mergeAssociations(),
              ownerId,
            ),
          ];
          if (isFollowUpExpanded) {
            const followUpFieldValues = this.loggerFollowUpTaskComponent?.getValues(crmObjectName) || [];
            apiCalls.push(
              this.activityLoggerService.addActivity(
                'Task',
                [...followUpFieldValues, manuallyLoggedFieldValue],
                this.mergeAssociations(),
                ownerId,
              ),
            );
          }
        }
        return combineLatest(apiCalls);
      }),
    );

    this.upsertActivityWorkState$.startWork(work$);
  }

  private getSelectedComponent(): LoggerComponentInterface | undefined {
    switch (this.activityTypeSelected) {
      case 'Note':
        return this.loggerNoteComponent;
      case 'Email':
        return this.loggerEmailComponent;
      case 'Call':
        return this.loggerCallComponent;
      case 'Meeting':
        return this.loggerMeetingComponent;
      case 'Task':
        return this.loggerTaskComponent;
      case 'Communication':
        return this.loggerCommunicationComponent;
      default:
        return;
    }
  }

  isFormValid(): boolean {
    return Boolean(this.getSelectedComponent()?.formGroup?.valid);
  }

  validateForm(): boolean {
    this.updateValueAndValidity();
    return this.isFormValid();
  }

  async setAssociationValues(associations: ActivityAssociationInterface[]): Promise<void> {
    const all = associations
      .filter((a) => !!a.crmObjectId)
      .map((a) => ({
        crmObjectId: a.crmObjectId,
        crmObjectType: a.crmObjectType as ObjectType | ObjectAssociationType,
      }));
    this.associations.set(all);
    this.contactAssociations = all.filter((item) => item.crmObjectType === 'Contact');
    this.companyAssociations = all.filter((item) => item.crmObjectType === 'Company');
    this.teamAssociations = all.filter((item) => item.crmObjectType === 'User');
    this.opportunityAssociations = all.filter((item) => item.crmObjectType === 'Opportunity');
  }

  updateValueAndValidity(): void {
    this.getSelectedComponent()?.validate();
  }

  expand(): void {
    this.collapsed.set(false);
  }

  collapse(): void {
    this.activityTypeSelected = undefined;
    this.collapsed.set(true);
    this.clearFields();
    this.activityTypeSelected = undefined;
    this.communicationSubTypeSelected = undefined;
  }

  followUpExpandedChanged(isExpanded: boolean): void {
    this.followUpExpanded$$.next(isExpanded);
  }

  handleCancelClicked(): void {
    this.collapse();
    this.cancel.emit();
  }

  handleDefaultStateFocus(): void {
    this.onActivityTypeChanged(DEFAULT_SELECTED_ACTIVITY_TYPE);
  }

  clearFields(): void {
    this.loggerNoteComponent?.resetForm();
    this.loggerEmailComponent?.resetForm();
    this.loggerCallComponent?.resetForm();
    this.loggerMeetingComponent?.resetForm();
    this.loggerTaskComponent?.resetForm();
    this.loggerCommunicationComponent?.resetForm();
    this.contactAssociations = [];
    this.companyAssociations = [];
    this.teamAssociations = [];
    this.opportunityAssociations = [];
    this.tags$$.next([]);
    this.sharedDescription = '';
  }

  private mergeAssociations(): ActivityAssociationInterface[] {
    const extraAssociations = this.extraAssociations || [];
    return [
      ...this.contactAssociations,
      ...this.companyAssociations,
      ...this.teamAssociations,
      ...this.opportunityAssociations,
      ...extraAssociations,
    ];
  }

  private getPrimaryAccountGroupId(crmObject: CrmObjectInterface): string | null {
    const fieldValue = this.fieldsService.getFieldValueFromCrmObject(
      crmObject,
      PlatformExtensionFieldIds.AccountGroupID,
    );
    return fieldValue?.stringValue ?? null;
  }

  private getObjectName(crmObject: CrmObjectInterface): string {
    const companyName = this.fieldsService.getFieldValueFromCrmObject(crmObject, StandardIds.CompanyName);
    if (companyName?.stringValue) {
      return companyName.stringValue;
    }

    const firstName = this.fieldsService.getFieldValueFromCrmObject(crmObject, StandardExternalIds.FirstName);
    const lastName = this.fieldsService.getFieldValueFromCrmObject(crmObject, StandardExternalIds.LastName);
    if (firstName?.stringValue || lastName?.stringValue) {
      return `${firstName?.stringValue ?? ''} ${lastName?.stringValue ?? ''}`.trim();
    }

    return crmObject.crmObjectId ?? '';
  }

  /**
   * Create a mobile layout observable.
   * If layout is set to `mobile`, it will always be `true`.
   * Otherwise, it will match the screen size  (<= `768px`).
   * @returns `Observable<boolean>`: `true` if the layout is mobile, `false` otherwise.
   */
  private setupMobileMatcher(): Observable<boolean> {
    const mobileMedia$ = this.breakpointObserver.observe(`(max-width: ${DEFAULT_MOBILE_BREAKPOINT})`).pipe(
      distinctUntilChanged(),
      map((result) => result.matches),
    );
    return this.layout$$.pipe(
      map((layout) => layout === 'mobile'),
      switchWhenFalsy(mobileMedia$),
    );
  }

  updateTags(tagEvent: string[]): void {
    this.tags$$?.next(tagEvent);
  }
}
